import { Device, Valve as ApiValve } from '@/types/api';
import { ValveNode, ValveMetrics, NodeError, DeviceToValveNodeTransformOptions, Valve as NodeValve } from './index';

/**
 * Transform a Device object to a ValveNode object
 */
export function transformDeviceToValveNode(
  device: Device,
  options: DeviceToValveNodeTransformOptions = {}
): ValveNode {
  const { includeMetrics = true, includeErrors = true, valves: deviceValves = [] } = options;

  // Extract device information from last_uplink_data
  const uplinkData = device.last_uplink_data;
  
  
  // Guard against missing uplink data
  if (!uplinkData) {
    console.error('❌ ERROR: Device missing last_uplink_data:', device.dev_eui);
    throw new Error(`Device ${device.dev_eui} missing last_uplink_data`);
  }

  // Create valve objects from device data, using valve aliases if available
  const valves: NodeValve[] = [
    {
      id: 1,
      name: getValveName(deviceValves, 1, 'Valve 1', device.id),
      alias: getValveAlias(deviceValves, 1, device.id),
      status: uplinkData.valve1_status === 'open' ? 'open' : 'closed',
      flowRate: calculateFlowRate(uplinkData.gpio1_pulse_count, device.last_seen_timestamp ?? 0),
      totalPulses: uplinkData.gpio1_pulse_count,
      settings: {
        timeControlEnabled: uplinkData.downlink_ack?.time_control_enabled || false,
        flowControlEnabled: uplinkData.downlink_ack?.flow_control_enabled || false,
      },
    },
    {
      id: 2,
      name: getValveName(deviceValves, 2, 'Valve 2', device.id),
      alias: getValveAlias(deviceValves, 2, device.id),
      status: uplinkData.valve2_status === 'open' ? 'open' : 'closed',
      flowRate: calculateFlowRate(uplinkData.gpio2_pulse_count, device.last_seen_timestamp ?? 0),
      totalPulses: uplinkData.gpio2_pulse_count,
      settings: {
        timeControlEnabled: uplinkData.downlink_ack?.time_control_enabled || false,
        flowControlEnabled: uplinkData.downlink_ack?.flow_control_enabled || false,
      },
    },
  ];

  // Generate errors based on device state
  const errors: NodeError[] = [];
  if (includeErrors) {
    // Check for low battery
    if (uplinkData.battery_level_percent < 20) {
      errors.push({
        id: `battery-${device.dev_eui}`,
        type: 'power',
        severity: uplinkData.battery_level_percent < 10 ? 'critical' : 'warning',
        message: `Low battery: ${uplinkData.battery_level_percent}%`,
        timestamp: Date.now(),
      });
    }

    // Check for poor signal
    if (uplinkData.rssi < -100) {
      errors.push({
        id: `signal-${device.dev_eui}`,
        type: 'communication',
        severity: uplinkData.rssi < -110 ? 'critical' : 'warning',
        message: `Poor signal strength: ${uplinkData.rssi} dBm`,
        timestamp: Date.now(),
      });
    }

    // Check for device offline
    const timeSinceLastSeen = Date.now() - (device.last_seen_timestamp ?? 0) * 1000;
    if (timeSinceLastSeen > 24 * 60 * 60 * 1000) { // 24 hours
      errors.push({
        id: `offline-${device.dev_eui}`,
        type: 'communication',
        severity: 'error',
        message: 'Device offline for more than 24 hours',
        timestamp: Date.now(),
      });
    }
  }

  // Create metrics object
  const metrics: ValveMetrics = includeMetrics ? {
    totalFlowToday: estimateDailyFlow(uplinkData.gpio1_pulse_count + uplinkData.gpio2_pulse_count),
    totalFlowWeek: estimateWeeklyFlow(uplinkData.gpio1_pulse_count + uplinkData.gpio2_pulse_count),
    totalFlowMonth: estimateMonthlyFlow(uplinkData.gpio1_pulse_count + uplinkData.gpio2_pulse_count),
    lastIrrigationDuration: estimateLastIrrigationDuration(device),
    averageFlowRate: calculateAverageFlowRate(valves),
    uptime: calculateUptime(device.last_seen_timestamp ?? 0),
  } : {
    totalFlowToday: 0,
    totalFlowWeek: 0,
    totalFlowMonth: 0,
    lastIrrigationDuration: 0,
    averageFlowRate: 0,
    uptime: 0,
  };

  // Determine online status
  const timeSinceLastSeen = Date.now() - (device.last_seen_timestamp ?? 0) * 1000;
  const isOnline = timeSinceLastSeen < 2 * 60 * 60 * 1000; // 2 hours

  return {
    id: device.dev_eui,
    name: device.alias || uplinkData.deviceName || `Device-${device.dev_eui.slice(-4)}`,
    alias: device.alias, // Store the alias separately for display logic
    location: extractLocationFromDevice(device),
    firmware: {
      version: uplinkData.software_version,
      hardware: uplinkData.hardware_version,
      protocol: uplinkData.protocol_version,
    },
    status: {
      online: isOnline,
      lastSeen: device.last_seen_timestamp ?? 0,
      batteryLevel: uplinkData.battery_level_percent,
      signalStrength: uplinkData.rssi,
    },
    valves,
    metrics,
    errors,
  };
}

/**
 * Transform multiple devices to valve nodes
 */
export function transformDevicesToValveNodes(
  devices: Device[] | { data: Device[] },
  options: DeviceToValveNodeTransformOptions = {}
): ValveNode[] {
  // Handle both array and {data: array} response formats
  const deviceArray = Array.isArray(devices) ? devices : devices?.data;
  
  if (!deviceArray || !Array.isArray(deviceArray)) {
    console.error('❌ ERROR: Invalid devices format for transformation:', devices);
    return [];
  }
  
  return deviceArray.map(device => {
    try {
      return transformDeviceToValveNode(device, options);
    } catch (error) {
      console.error('❌ ERROR: Failed to transform device:', device?.dev_eui, error);
      // Return a minimal fallback node to prevent complete failure
      return {
        id: device?.dev_eui || 'unknown',
        name: device?.alias || device?.name || `Device-${(device?.dev_eui || '').slice(-4)}`,
        alias: device?.alias, // Store the alias separately for display logic
        location: undefined,
        firmware: {
          version: 'unknown',
          hardware: 'unknown',
          protocol: 'unknown',
        },
        status: {
          online: false,
          lastSeen: 0,
          batteryLevel: 0,
          signalStrength: 0,
        },
        valves: [
          {
            id: 1,
            name: 'Valve 1',
            status: 'closed' as const,
            flowRate: 0,
            totalPulses: 0,
            settings: {
              timeControlEnabled: false,
              flowControlEnabled: false,
            },
          },
          {
            id: 2,
            name: 'Valve 2',
            status: 'closed' as const,
            flowRate: 0,
            totalPulses: 0,
            settings: {
              timeControlEnabled: false,
              flowControlEnabled: false,
            },
          },
        ],
        metrics: {
          totalFlowToday: 0,
          totalFlowWeek: 0,
          totalFlowMonth: 0,
          lastIrrigationDuration: 0,
          averageFlowRate: 0,
          uptime: 0,
        },
        errors: [{
          id: `transform-error-${device?.dev_eui || Date.now()}`,
          type: 'system' as const,
          severity: 'error' as const,
          message: `Failed to transform device data: ${error instanceof Error ? error.message : 'Unknown error'}`,
          timestamp: Date.now(),
        }],
      };
    }
  });
}

/**
 * Helper function to get valve name with fallback logic
 */
function getValveName(valves: ApiValve[] | null | undefined, valveNumber: number, fallbackName: string, deviceId?: number): string {
  // Ensure valves is an array before calling .find()
  if (!Array.isArray(valves)) {
    return fallbackName;
  }
  
  // First try to find valve by both device_id and valve_number for exact match
  let valve = deviceId ? valves.find(v => v.device_id === deviceId && v.valve_number === valveNumber) : null;
  
  // If not found, try by valve_number only (fallback for backward compatibility)
  if (!valve) {
    console.log('🔍 DIAGNOSTIC: getValveName - Exact match not found, trying valve_number only');
    valve = valves.find(v => v.valve_number === valveNumber);
  }
  
  const result = valve?.name || fallbackName;
  
  return result;
}

/**
 * Helper function to get valve alias with fallback logic
 */
function getValveAlias(valves: ApiValve[] | null | undefined, valveNumber: number, deviceId?: number): string | undefined {
  // Ensure valves is an array before calling .find()
  if (!Array.isArray(valves)) {
    return undefined;
  }
  
  // First try to find valve by both device_id and valve_number for exact match
  let valve = deviceId ? valves.find(v => v.device_id === deviceId && v.valve_number === valveNumber) : null;
  
  // If not found, try by valve_number only (fallback for backward compatibility)
  if (!valve) {
    console.log('🔍 DIAGNOSTIC: getValveAlias - Exact match not found, trying valve_number only');
    valve = valves.find(v => v.valve_number === valveNumber);
  }
  
  const result = valve?.name; // Use the name field from API as the alias
  
  return result;
}

/**
 * Calculate flow rate based on pulse count and time
 */
function calculateFlowRate(pulseCount: number, lastSeenTimestamp: number): number {
  // This is a simplified calculation - in a real implementation,
  // you would use the time difference between pulse counts
  const timeDiff = Date.now() - lastSeenTimestamp * 1000;
  const hoursDiff = timeDiff / (1000 * 60 * 60);
  
  if (hoursDiff <= 0) return 0;
  
  // Assuming each pulse represents 0.1 liters and the pulses occurred over the last hour
  const estimatedPulsesPerHour = pulseCount / Math.max(hoursDiff, 1);
  return Math.round(estimatedPulsesPerHour * 0.1 * 10) / 10; // Round to 1 decimal place
}

/**
 * Estimate daily flow based on pulse count
 */
function estimateDailyFlow(totalPulses: number): number {
  // Assuming each pulse represents 0.1 liters
  // This is a simplified estimation - in reality, you'd track daily pulses
  return Math.round(totalPulses * 0.1);
}

/**
 * Estimate weekly flow based on pulse count
 */
function estimateWeeklyFlow(totalPulses: number): number {
  // Assuming each pulse represents 0.1 liters
  return Math.round(totalPulses * 0.1 * 7);
}

/**
 * Estimate monthly flow based on pulse count
 */
function estimateMonthlyFlow(totalPulses: number): number {
  // Assuming each pulse represents 0.1 liters
  return Math.round(totalPulses * 0.1 * 30);
}

/**
 * Estimate last irrigation duration based on device data
 */
function estimateLastIrrigationDuration(device: Device): number {
  // Check if there was a recent downlink command
  if (device.pending_downlink_command && device.pending_downlink_timestamp) {
    const timeDiff = Date.now() - device.pending_downlink_timestamp * 1000;
    const minutesDiff = timeDiff / (1000 * 60);
    
    // If the command was for opening a valve with time control
    const command = device.pending_downlink_command;
    if (command.time_control_enabled && command.time_control_seconds) {
      return Math.min(command.time_control_seconds / 60, minutesDiff);
    }
  }
  
  return 0;
}

/**
 * Calculate average flow rate across all valves
 */
function calculateAverageFlowRate(valves: NodeValve[]): number {
  const activeValves = valves.filter(v => v.status === 'open' && v.flowRate !== undefined);
  if (activeValves.length === 0) return 0;
  
  const totalFlowRate = activeValves.reduce((sum, valve) => sum + (valve.flowRate || 0), 0);
  return Math.round(totalFlowRate / activeValves.length * 10) / 10;
}

/**
 * Calculate device uptime based on last seen timestamp
 */
function calculateUptime(lastSeenTimestamp: number): number {
  // This is a simplified calculation - in reality, you'd track actual uptime
  const deviceAge = Date.now() - (lastSeenTimestamp - 30 * 24 * 60 * 60 * 1000); // Assume device is 30 days old
  return Math.max(0, Math.round(deviceAge / (1000 * 60 * 60)));
}

/**
 * Extract location information from device data
 */
function extractLocationFromDevice(_device: Device): string | undefined {
  // In a real implementation, this might come from device metadata
  // For now, we'll return undefined to indicate no location is set
  return undefined;
}

/**
 * Update a ValveNode with new device data (for WebSocket updates)
 */
export function updateValveNodeFromDevice(
  existingNode: ValveNode,
  deviceUpdate: Partial<Device>
): ValveNode {
  if (!deviceUpdate.last_uplink_data) {
    return existingNode;
  }

  const uplinkData = deviceUpdate.last_uplink_data;
  
  // Update valve statuses
  const updatedValves: NodeValve[] = existingNode.valves.map(valve => {
    if (valve.id === 1) {
      return {
        ...valve,
        status: (uplinkData.valve1_status === 'open' ? 'open' : 'closed') as 'open' | 'closed',
        flowRate: calculateFlowRate(uplinkData.gpio1_pulse_count, deviceUpdate.last_seen_timestamp || existingNode.status.lastSeen),
        totalPulses: uplinkData.gpio1_pulse_count,
      };
    } else if (valve.id === 2) {
      return {
        ...valve,
        status: (uplinkData.valve2_status === 'open' ? 'open' : 'closed') as 'open' | 'closed',
        flowRate: calculateFlowRate(uplinkData.gpio2_pulse_count, deviceUpdate.last_seen_timestamp || existingNode.status.lastSeen),
        totalPulses: uplinkData.gpio2_pulse_count,
      };
    }
    return valve;
  });

  // Determine online status
  const lastSeen = deviceUpdate.last_seen_timestamp || existingNode.status.lastSeen;
  const timeSinceLastSeen = Date.now() - lastSeen * 1000;
  const isOnline = timeSinceLastSeen < 2 * 60 * 60 * 1000; // 2 hours

  return {
    ...existingNode,
    status: {
      ...existingNode.status,
      online: isOnline,
      lastSeen,
      batteryLevel: uplinkData.battery_level_percent,
      signalStrength: uplinkData.rssi,
    },
    valves: updatedValves,
    metrics: {
      ...existingNode.metrics,
      averageFlowRate: calculateAverageFlowRate(updatedValves),
      uptime: calculateUptime(lastSeen),
    },
  };
}